import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { BigButton } from "../design/BigButton";

function assetUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/uploads/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  if (u.startsWith("/uploads/")) return `${process.env.REACT_APP_BACKEND_URL}/api${u}`;
  return u;
}

// ---------------------------------------------------------------------------
// AI Scene Generator
// ---------------------------------------------------------------------------

export function AiSceneGenerator() {
  const [prompt, setPrompt] = useState(
    "A cheerful sunny park with a wooden bench, red kite, blue ball, ice-cream cart, dog on a leash, and a small pond with ducks."
  );
  const [stylePreset, setStylePreset] = useState("hand-drawn watercolor, warm palette");
  const [aspectRatio, setAspectRatio] = useState("landscape");
  const [variations, setVariations] = useState(1);
  const [generateThumbs, setGenerateThumbs] = useState(false);
  const [hints, setHints] = useState([]);
  const [hintDraft, setHintDraft] = useState("");
  const [worlds, setWorlds] = useState([]);
  const [worldId, setWorldId] = useState("");
  const [difficulty, setDifficulty] = useState("easy");
  const [levelName, setLevelName] = useState("Sunny Park");
  const [busy, setBusy] = useState(false);
  const [connOk, setConnOk] = useState(null);
  const [connMsg, setConnMsg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(0);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/worlds");
        setWorlds(data.items || []);
        if (data.items?.length && !worldId) setWorldId(data.items[0].id);
      } catch (_e) {
        // Non-fatal — user can still generate without a world context.
      }
    })();
  }, []);

  async function testConnection() {
    setConnOk(null); setConnMsg(null);
    try {
      const { data } = await api.post("/admin/ai/test-connection");
      setConnOk(!!data.ok);
      setConnMsg(data.ok ? `OK · model=${data.model}` : (data.reason || "Not configured."));
    } catch (e) {
      setConnOk(false);
      setConnMsg(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }

  function addHint() {
    const v = hintDraft.trim();
    if (!v) return;
    setHints((prev) => [...prev, v]);
    setHintDraft("");
  }
  function removeHint(i) { setHints((prev) => prev.filter((_, idx) => idx !== i)); }

  async function generate() {
    setBusy(true); setErr(null); setMsg(null); setPreview(null); setSelectedVariation(0);
    try {
      const { data } = await api.post("/admin/ai/generate-scene", {
        prompt, concept: prompt, stylePreset, aspectRatio,
        objectHints: hints, variations, generateObjectThumbnails: generateThumbs,
      });
      setPreview(data);
      const nThumbs = (data.objectThumbnails || []).filter((t) => t.url).length;
      setMsg(
        `Generated ${data.variations?.length || 1} scene(s)` +
        (nThumbs ? ` and ${nThumbs} object thumbnail(s)` : "") +
        ". Review before saving."
      );
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.reason) {
        setErr(`${detail.error || "AI error"}: ${detail.reason}${detail.hint ? " — " + detail.hint : ""}`);
      } else {
        setErr(formatApiErrorDetail(detail || e.message));
      }
    } finally { setBusy(false); }
  }

  async function saveToLibrary() {
    setMsg(`Kept in media library as ${activeVariation()?.filename || preview?.filename}. Available for hand-authored levels.`);
  }

  function activeVariation() {
    if (!preview) return null;
    const variations = preview.variations || [];
    return variations[selectedVariation] || { url: preview.url, filename: preview.filename };
  }

  async function createDraftLevel() {
    if (!preview) return;
    if (!worldId) { setErr("Pick a world first."); return; }
    setBusy(true); setErr(null);
    try {
      const chosen = activeVariation();
      const { data } = await api.post("/admin/ai/create-draft-level", {
        name: levelName || "New AI Level",
        worldId,
        sceneImageUrl: chosen.url,
        difficulty,
        objectNames: hints,
        objectThumbnails: preview.objectThumbnails || [],
      });
      setMsg(`Draft level created (${data.objectCount} placeholder hitboxes). Open it in the editor to fine-tune.`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  return (
    <div data-testid="ai-scene-generator">
      <div className="admin-header">
        <div>
          <h1>AI Scene Generator</h1>
          <p className="admin-header__sub">Gemini nano-banana (<code>gemini-3-pro-image-preview</code>). Nothing is saved as content until you explicitly click a save button.</p>
        </div>
        <div className="admin-actions">
          <BigButton variant="ghost" onClick={testConnection} data-testid="ai-test-connection">Test connection</BigButton>
          {connOk === true && <span className="admin-badge admin-badge--published" data-testid="ai-conn-ok">connected</span>}
          {connOk === false && <span className="admin-badge admin-badge--draft" data-testid="ai-conn-fail">unavailable</span>}
        </div>
      </div>
      {connMsg && (
        <div className={connOk ? "admin-ok" : "admin-warning"} data-testid="ai-conn-msg">{connMsg}</div>
      )}
      {err && <div className="admin-error" data-testid="ai-error">{err}</div>}
      {msg && <div className="admin-ok" data-testid="ai-msg">{msg}</div>}

      <div className="ai-gen">
        <section className="admin-form">
          <label>Prompt / Concept
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              data-testid="ai-prompt"
            />
          </label>
          <div className="editor-field-row">
            <label>Style preset
              <input
                value={stylePreset}
                onChange={(e) => setStylePreset(e.target.value)}
                placeholder="hand-drawn watercolor, warm palette"
                data-testid="ai-style"
              />
            </label>
            <label>Aspect ratio
              <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} data-testid="ai-aspect">
                <option value="landscape">Landscape (scenes)</option>
                <option value="square">Square (thumbnails)</option>
              </select>
            </label>
          </div>
          <div className="editor-field-row">
            <label>Variations
              <select value={variations} onChange={(e) => setVariations(parseInt(e.target.value))} data-testid="ai-variations">
                <option value={1}>1 scene</option>
                <option value={2}>2 scenes</option>
                <option value={3}>3 scenes (batch)</option>
              </select>
            </label>
            <label style={{ flexDirection: "row", alignItems: "center", gap: "0.5rem", justifyContent: "flex-start", paddingTop: 22 }}>
              <input
                type="checkbox"
                checked={generateThumbs}
                onChange={(e) => setGenerateThumbs(e.target.checked)}
                data-testid="ai-gen-thumbs"
              />
              Generate matching object thumbnails
            </label>
          </div>
          <label>Target object hints (optional)
            <div style={{ display: "flex", gap: 6 }}>
              <input value={hintDraft} onChange={(e) => setHintDraft(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHint(); } }}
                     placeholder="e.g. red kite" data-testid="ai-hint-input" />
              <BigButton variant="ghost" onClick={addHint} data-testid="ai-hint-add">Add</BigButton>
            </div>
          </label>
          {hints.length > 0 && (
            <div className="ai-hint-list" data-testid="ai-hint-list">
              {hints.map((h, i) => (
                <span key={i} className="ai-hint-list__chip" data-testid={`ai-hint-${i}`}>
                  {h}
                  <button aria-label={`remove ${h}`} onClick={() => removeHint(i)}>×</button>
                </span>
              ))}
            </div>
          )}
          <BigButton onClick={generate} disabled={busy} data-testid="ai-generate">
            {busy ? "Generating…" : "Generate scene"}
          </BigButton>
          <div className="admin-info">
            Nothing is created automatically. After preview, choose <b>Save to media library</b> to keep the image only, or <b>Create draft level</b> to also make an unpublished level with a starter grid of hitboxes based on your object hints.
          </div>
        </section>

        <section className="ai-gen__preview">
          <h2 style={{ margin: 0 }}>Preview</h2>
          {preview ? (
            <>
              {(preview.variations || []).length > 1 && (
                <div role="radiogroup" aria-label="Pick your favorite variation" className="ai-gen__variations" data-testid="ai-variations-list">
                  {(preview.variations || []).map((v, i) => (
                    <label key={v.filename} className={`ai-gen__variation ${selectedVariation === i ? "selected" : ""}`}>
                      <input
                        type="radio" name="ai-variation" checked={selectedVariation === i}
                        onChange={() => setSelectedVariation(i)}
                        data-testid={`ai-variation-${i}`}
                      />
                      <img src={assetUrl(v.url)} alt={`Variation ${i + 1}`} />
                      <span>#{i + 1}</span>
                    </label>
                  ))}
                </div>
              )}
              <img src={assetUrl(activeVariation()?.url)} alt="Generated scene" data-testid="ai-preview" />
              <div className="ai-gen__meta">
                Saved as <code>{activeVariation()?.filename}</code> · model {preview.model}
                {preview.warnings?.length ? <div>Warnings: {preview.warnings.join("; ")}</div> : null}
              </div>
              {(preview.objectThumbnails || []).length > 0 && (
                <div data-testid="ai-object-thumbnails">
                  <h3 style={{ margin: "0.5rem 0" }}>Matching object thumbnails</h3>
                  <div className="media-grid">
                    {preview.objectThumbnails.map((t) => (
                      <div key={t.name} className="media-tile" data-testid={`ai-thumb-${t.name}`}>
                        {t.url ? <img src={assetUrl(t.url)} alt={t.name} /> : <div className="ai-gen__placeholder" style={{ aspectRatio: "1/1" }}>{t.reason || "?"}</div>}
                        <div className="media-tile__filename">{t.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="admin-form">
                <label>Level name<input value={levelName} onChange={(e) => setLevelName(e.target.value)} data-testid="ai-draft-name" /></label>
                <div className="editor-field-row">
                  <label>World
                    <select value={worldId} onChange={(e) => setWorldId(e.target.value)} data-testid="ai-draft-world">
                      {worlds.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                  </label>
                  <label>Difficulty
                    <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} data-testid="ai-draft-difficulty">
                      <option value="easy">easy</option>
                      <option value="medium">medium</option>
                      <option value="hard">hard</option>
                    </select>
                  </label>
                </div>
              </div>
              <div className="admin-actions">
                <BigButton variant="ghost" onClick={saveToLibrary} data-testid="ai-save-to-library">Save to media library</BigButton>
                <BigButton onClick={createDraftLevel} disabled={busy} data-testid="ai-create-draft">Create draft level</BigButton>
              </div>
            </>
          ) : (
            <div className="ai-gen__placeholder">Your generated scene will appear here for review.</div>
          )}
        </section>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Media Library
// ---------------------------------------------------------------------------

export function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/admin/media/list");
      setItems(data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }
  useEffect(() => { load(); }, []);

  async function upload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  return (
    <div data-testid="media-library">
      <div className="admin-header">
        <div><h1>Media library</h1><p className="admin-header__sub">{items.length} files in /uploads</p></div>
        <label className="wmleye-bigbtn wmleye-bigbtn--primary" style={{ cursor: "pointer" }}>
          Upload file
          <input type="file" hidden onChange={upload} disabled={busy} data-testid="media-upload" accept="image/*,audio/*" />
        </label>
      </div>
      {err && <div className="admin-error">{err}</div>}
      <div className="media-grid">
        {items.map((m) => (
          <div key={m.filename} className="media-tile" data-testid={`media-tile-${m.filename}`}>
            {m.kind === "image" ? (
              <img src={assetUrl(m.url)} alt={m.filename} loading="lazy" />
            ) : (
              <div className="ai-gen__placeholder" style={{ aspectRatio: "1 / 1" }}>{m.kind}</div>
            )}
            <div className="media-tile__filename">{m.filename}</div>
            <div>{(m.size / 1024).toFixed(0)} KB</div>
          </div>
        ))}
        {items.length === 0 && <div style={{ color: "var(--wmleye-ink-soft)" }}>No uploads yet.</div>}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Learning object library
// ---------------------------------------------------------------------------

export function LearningObjectLibrary() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");
  const [newLo, setNewLo] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/admin/learning-objects");
      setItems(data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((x) =>
      (x.displayName || "").toLowerCase().includes(q) ||
      (x.canonicalName || "").toLowerCase().includes(q) ||
      (x.category || "").toLowerCase().includes(q));
  }, [items, query]);

  async function create() {
    if (!newLo?.canonicalName || !newLo?.displayName) return;
    try {
      await api.post("/admin/learning-objects", {
        canonicalName: newLo.canonicalName,
        displayName: newLo.displayName,
        spokenName: newLo.spokenName || newLo.displayName.toLowerCase(),
        spellingSequence: [...newLo.displayName.toUpperCase()].map((c) => c === "-" ? " " : c),
        category: newLo.category || "custom",
        thumbnailUrl: newLo.thumbnailUrl || "",
        exampleSentence: newLo.exampleSentence || `I see a ${newLo.displayName.toLowerCase()}!`,
      });
      setNewLo(null);
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }

  return (
    <div data-testid="lo-library">
      <div className="admin-header">
        <div><h1>Learning objects</h1><p className="admin-header__sub">{items.length} words</p></div>
        <div className="admin-actions">
          <input placeholder="Filter…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="lo-filter" />
          <BigButton onClick={() => setNewLo({ category: "custom" })} data-testid="lo-add">+ Add</BigButton>
        </div>
      </div>
      {err && <div className="admin-error">{err}</div>}
      <table className="admin-table">
        <thead><tr><th>Thumb</th><th>Display</th><th>Canonical</th><th>Category</th><th>Spelling</th></tr></thead>
        <tbody>
          {filtered.map((lo) => (
            <tr key={lo.id} data-testid={`lo-row-${lo.canonicalName}`}>
              <td>{lo.thumbnailUrl ? <img src={assetUrl(lo.thumbnailUrl)} alt="" width={48} height={48} style={{ borderRadius: 8 }} /> : "—"}</td>
              <td>{lo.displayName}</td>
              <td><code>{lo.canonicalName}</code></td>
              <td>{lo.category}</td>
              <td><small>{(lo.spellingSequence || []).join("")}</small></td>
            </tr>
          ))}
        </tbody>
      </table>

      {newLo && (
        <div className="modal" onClick={() => setNewLo(null)}>
          <div className="modal__panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520, background: "#fff", padding: "1.25rem", borderRadius: 16 }}>
            <h2 style={{ margin: 0 }}>New learning object</h2>
            <div className="admin-form">
              <label>Canonical name (snake_case)<input value={newLo.canonicalName || ""} onChange={(e) => setNewLo({ ...newLo, canonicalName: e.target.value.replace(/[^a-z0-9_]/g, "_") })} data-testid="lo-form-canonical" /></label>
              <label>Display name<input value={newLo.displayName || ""} onChange={(e) => setNewLo({ ...newLo, displayName: e.target.value })} data-testid="lo-form-display" /></label>
              <label>Category<input value={newLo.category || ""} onChange={(e) => setNewLo({ ...newLo, category: e.target.value })} data-testid="lo-form-category" /></label>
              <label>Thumbnail URL<input value={newLo.thumbnailUrl || ""} onChange={(e) => setNewLo({ ...newLo, thumbnailUrl: e.target.value })} /></label>
              <label>Example sentence<input value={newLo.exampleSentence || ""} onChange={(e) => setNewLo({ ...newLo, exampleSentence: e.target.value })} /></label>
            </div>
            <div className="modal__actions" style={{ marginTop: "0.75rem" }}>
              <BigButton variant="ghost" onClick={() => setNewLo(null)}>Cancel</BigButton>
              <BigButton onClick={create} data-testid="lo-form-save">Create</BigButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Support inbox
// ---------------------------------------------------------------------------

export function SupportInbox() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  async function load() {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await api.get("/admin/support-requests", { params });
      setItems(data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }
  useEffect(() => { load(); }, [statusFilter]);

  async function update(id, patch) {
    try {
      await api.patch(`/admin/support-requests/${id}`, patch);
      load();
      if (selected?.id === id) setSelected({ ...selected, ...patch });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }

  return (
    <div data-testid="support-inbox">
      <div className="admin-header">
        <div><h1>Support inbox</h1><p className="admin-header__sub">{items.length} shown</p></div>
        <label>Status
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="support-filter">
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </label>
      </div>
      {err && <div className="admin-error">{err}</div>}
      <table className="admin-table">
        <thead><tr><th>When</th><th>From</th><th>Subject</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} data-testid={`support-row-${r.id}`}>
              <td><small>{(r.createdAt || "").replace("T", " ").slice(0, 19)}</small></td>
              <td>{r.email}</td>
              <td>{r.subject}</td>
              <td><span className={`admin-badge ${r.status === "resolved" ? "admin-badge--published" : r.status === "in_progress" ? "admin-badge--playable" : "admin-badge--draft"}`}>{r.status}</span></td>
              <td className="admin-actions">
                <BigButton variant="ghost" onClick={() => setSelected(r)} data-testid={`support-view-${r.id}`}>View</BigButton>
                {r.status !== "resolved" && (
                  <BigButton onClick={() => update(r.id, { status: "resolved" })} data-testid={`support-resolve-${r.id}`}>Resolve</BigButton>
                )}
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan="5" style={{ color: "var(--wmleye-ink-soft)" }}>No requests.</td></tr>}
        </tbody>
      </table>

      {selected && (
        <div className="modal" onClick={() => setSelected(null)}>
          <div className="modal__panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600, background: "#fff", padding: "1.25rem", borderRadius: 16 }}>
            <h2 style={{ margin: 0 }}>{selected.subject}</h2>
            <p style={{ color: "var(--wmleye-ink-soft)" }}>From <b>{selected.email}</b> · {selected.createdAt}</p>
            <div style={{ whiteSpace: "pre-wrap", background: "var(--wmleye-cream-2)", padding: "0.75rem", borderRadius: 12 }}>
              {selected.message}
            </div>
            <div className="modal__actions" style={{ marginTop: "0.75rem" }}>
              <BigButton variant="ghost" onClick={() => setSelected(null)}>Close</BigButton>
              <BigButton onClick={() => update(selected.id, { status: "in_progress" })}>Mark in progress</BigButton>
              <BigButton onClick={() => update(selected.id, { status: "resolved" })}>Resolve</BigButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

export function AuditLog() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/audit-logs", { params: { limit: 200 } });
        setItems(data.items || []);
      } catch (e) {
        setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
      }
    })();
  }, []);

  return (
    <div data-testid="audit-log">
      <div className="admin-header">
        <div><h1>Audit log</h1><p className="admin-header__sub">Last {items.length} events</p></div>
      </div>
      {err && <div className="admin-error">{err}</div>}
      <table className="admin-table" data-testid="audit-log-table">
        <thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Target</th><th>Payload</th></tr></thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id} data-testid={`audit-row-${a.id}`}>
              <td><small>{(a.createdAt || "").replace("T", " ").slice(0, 19)}</small></td>
              <td><small>{a.actorEmail}</small></td>
              <td><code>{a.action}</code></td>
              <td><small>{a.targetCollection}/{a.targetId?.slice(0, 8) || "—"}</small></td>
              <td><small><code>{safeStringify(a.payload)}</code></small></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function safeStringify(v) {
  try {
    const s = JSON.stringify(v);
    return s && s.length > 120 ? s.slice(0, 117) + "…" : s || "";
  } catch (_e) { return ""; }
}


// ---------------------------------------------------------------------------
// App settings editor
// ---------------------------------------------------------------------------

export function AppSettingsEditor() {
  const [settings, setSettings] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/admin/app-settings");
      setSettings(data.appSettings);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    try {
      await api.patch("/admin/app-settings", {
        narrationSpeed: settings.narrationSpeed,
        music: settings.music,
        soundEffects: settings.soundEffects,
        reducedMotion: settings.reducedMotion,
        highContrast: settings.highContrast,
        analyticsConsent: settings.analyticsConsent,
        premiumUnlockPriceUsd: settings.premiumUnlockPriceUsd,
        premiumUnlockEnabled: settings.premiumUnlockEnabled,
      });
      setMsg("Saved.");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }

  if (!settings) return <p>Loading…</p>;

  return (
    <div data-testid="app-settings-editor">
      <div className="admin-header"><div><h1>App settings</h1></div></div>
      {err && <div className="admin-error">{err}</div>}
      {msg && <div className="admin-ok">{msg}</div>}
      <div className="admin-form">
        <div className="editor-field-row">
          <label>Narration speed
            <select value={settings.narrationSpeed} onChange={(e) => setSettings({ ...settings, narrationSpeed: e.target.value })} data-testid="settings-narration">
              <option value="slow">slow</option>
              <option value="normal">normal</option>
              <option value="fast">fast</option>
            </select>
          </label>
          <label>Music
            <select value={settings.music} onChange={(e) => setSettings({ ...settings, music: e.target.value })}>
              <option value="on">on</option>
              <option value="off">off</option>
            </select>
          </label>
        </div>
        <div className="editor-field-row">
          <label>Sound effects
            <select value={settings.soundEffects} onChange={(e) => setSettings({ ...settings, soundEffects: e.target.value })}>
              <option value="on">on</option>
              <option value="off">off</option>
            </select>
          </label>
          <label>Premium price (USD)
            <input type="number" step="0.01" value={settings.premiumUnlockPriceUsd || 0}
                   onChange={(e) => setSettings({ ...settings, premiumUnlockPriceUsd: parseFloat(e.target.value || "0") })}
                   data-testid="settings-premium-price" />
          </label>
        </div>
        <label style={{ flexDirection: "row", alignItems: "center", gap: "0.5rem" }}>
          <input type="checkbox" checked={!!settings.premiumUnlockEnabled} onChange={(e) => setSettings({ ...settings, premiumUnlockEnabled: e.target.checked })} data-testid="settings-premium-enabled" />
          Premium unlock enabled
        </label>
        <label style={{ flexDirection: "row", alignItems: "center", gap: "0.5rem" }}>
          <input type="checkbox" checked={!!settings.reducedMotion} onChange={(e) => setSettings({ ...settings, reducedMotion: e.target.checked })} />
          Reduced motion (default)
        </label>
        <label style={{ flexDirection: "row", alignItems: "center", gap: "0.5rem" }}>
          <input type="checkbox" checked={!!settings.highContrast} onChange={(e) => setSettings({ ...settings, highContrast: e.target.checked })} />
          High contrast (default)
        </label>
        <label style={{ flexDirection: "row", alignItems: "center", gap: "0.5rem" }}>
          <input type="checkbox" checked={!!settings.analyticsConsent} onChange={(e) => setSettings({ ...settings, analyticsConsent: e.target.checked })} />
          Analytics consent (default)
        </label>
        <BigButton onClick={save} data-testid="settings-save">Save</BigButton>
      </div>
    </div>
  );
}
